import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useMyMApi from '../../hooks/useMyMApi';
import useValidationRules from '../../hooks/useValidationRules';
import useWebContext from '../../hooks/useWebContext';
import { MyMButton } from '../../ui-elements/MyMButton';
import { MyMLabel, MyMVariantLabel } from '../../ui-elements/MyMLabel';
import { MyMProgress } from '../../ui-elements/MyMProgress';
import ValidatableDatePicker from '../../ui-parts/ValidatableDatePicker';
import ValidatableTextBox from "../../ui-parts/ValidatableTextBox";

/** MyMine Version2.0 Notification機能追加
 * NotificationRegistration Page Component
 */
const NotificationRegistration = (props) => {
  const { notificationId, handleModalClose, callerRefresh } = props;
  const screenId = 'MyM209';
  const apiRequest = useMyMApi(screenId);
  const [isLoading, setIsLoading] = useState(false);
  const [notificationInfo, setNotificationInfo] = useState(null);
  const validationRules = useValidationRules();
  const { formState, control, handleSubmit, trigger } = useForm({
    mode: 'onSubmit',
  })
  const { controlSettings, messageList } = useWebContext(screenId);

  useEffect(() => {
    const callbackLoadSuccess = (result) => {
      setNotificationInfo(result.NotifyDispInfo ?? {});

      // 新規モードの場合
      if (notificationId === null) {
        // 公開期間のデフォルト値を設定(本日 ～ 本日 + 30日)
        const today = new Date();
        result.NotifyDispInfo.DurationFrom = today.toLocaleDateString();
        result.NotifyDispInfo.DurationTo = new Date(today.setDate(today.getDate() + 30)).toLocaleDateString();
      }
    }
    const callbackLoadError = (result) => {
      handleModalClose();
    }
    apiRequest.requestJson(`api/notification/registration-load?notificationId=${notificationId != null ? notificationId : ''}`, 'GET', callbackLoadSuccess, callbackLoadError)
  }, []);

  /**
   * Publish処理
   */
  const handlePublish = () => {
    if (!window.confirm(messageList.MyM_MSG_I_0119)) return;

    setIsLoading(true);
    const callbackSuccess = () => {
      handleModalClose();
      window.alert(messageList.MyM_MSG_I_0066);
      setIsLoading(false);
      callerRefresh();
    }
    const callbackFailed = () => {
      setIsLoading(false);
    }

    const requestBody = { ...notificationInfo, }
    if (notificationId) requestBody['NotificationId'] = notificationId;
    apiRequest.requestJson(`api/notification/publish`, 'POST', callbackSuccess, callbackFailed, requestBody)
  };

  const onError = () => {
    window.alert(messageList.MyM_MSG_E_0003);
  };

  /**
   * Delete押下時処理
   */
  const onClickDelete = () => {

    if (!window.confirm(messageList.MyM_MSG_I_0118)) return;
    setIsLoading(true);
    const callbackSuccess = () => {
      handleModalClose();
      window.alert(messageList.MyM_MSG_I_0009);
      setIsLoading(false);
      callerRefresh();
    }
    const callbackFailed = () => {
      setIsLoading(false);
    }

    const body = { 'notificationId': notificationId };
    apiRequest.requestJson(`api/notification/delete`, 'POST', callbackSuccess, callbackFailed, body)
  };

  if (notificationInfo === null)
    return <MyMProgress />

  return (
    <Box className='p-1'>
      {isLoading && <MyMProgress />}
      <Grid container rowSpacing={2.5} >
        <Grid item xs={12} md={1} className='d-flex align-items-start' sx={{ mt: 1 }}>
          <MyMLabel>{controlSettings.LabelList.lblDurationTitle}</MyMLabel>
        </Grid>
        <Grid item xs={12} md={11}>
          <Stack direction='row' spacing={3} className='d-flex align-items-start'>
            <ValidatableDatePicker
              name="durationFrom"
              onChange={
                (newValue) => {
                  setNotificationInfo({ ...notificationInfo, "DurationFrom": newValue !== null ? newValue?.$d.toLocaleString() : null })
                  if (formState.isSubmitted)
                    setTimeout(() => { trigger("durationTo") }, 0);
                }
              }
              width="200px"
              value={notificationInfo.DurationFrom ? dayjs(notificationInfo.DurationFrom) : notificationInfo.DurationFrom}
              control={control}
              formState={formState}
              rules={{
                ...validationRules.date(`${controlSettings.LabelList.lblDurationTitle} From`, true),
              }}
            />
            <MyMLabel className='mt-2'>～</MyMLabel>
            <ValidatableDatePicker
              name="durationTo"
              onChange={
                (newValue) => setNotificationInfo({ ...notificationInfo, "DurationTo": newValue !== null ? newValue?.$d.toLocaleString() : null })
              }
              width="200px"
              value={notificationInfo.DurationTo ? dayjs(notificationInfo.DurationTo) : notificationInfo.DurationTo}
              control={control}
              formState={formState}
              rules={{
                ...validationRules.date(`${controlSettings.LabelList.lblDurationTitle} To`, true, notificationInfo.DurationFrom),
              }}
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <MyMLabel>
            {controlSettings.LabelList.lblNotificationTitleTitle}
          </MyMLabel>
          <ValidatableTextBox
            control={control}
            name="notificationTitle"
            maxLength={100}
            formState={formState}
            defaultValue={notificationInfo.Title}
            rules={
              {
                ...validationRules.required(),
                ...validationRules.maxLength(100),
              }
            }
            onChange={(e) => { setNotificationInfo({ ...notificationInfo, Title: e.target.value }) }}
          />
        </Grid>
        <Grid item xs={12}>
          <Stack>
            <MyMLabel className='d-flex justify-content-between'>
              {controlSettings.LabelList.lblNotificationTitle}
              <MyMVariantLabel variant="caption">{notificationInfo.Message?.length ?? 0} / 300</MyMVariantLabel>
            </MyMLabel>
          </Stack>
          <ValidatableTextBox
            multiline
            rows={8}
            control={control}
            name="notificationMessage"
            maxLength={300}
            formState={formState}
            defaultValue={notificationInfo.Message ? notificationInfo.Message : ""}
            rules={
              {
                ...validationRules.required(),
                ...validationRules.maxLength(300),
              }
            }
            placeholder={controlSettings.LabelList.lblNotificationPlaceholder}
            onChange={(e) => { setNotificationInfo({ ...notificationInfo, Message: e.target.value }) }}
          />

        </Grid>
        <Grid item xs={12}>
          <MyMLabel>{controlSettings.LabelList.lblUrlTitle}</MyMLabel>
          <ValidatableTextBox
            control={control}
            name="url"
            maxLength={250}
            formState={formState}
            defaultValue={notificationInfo.Url ? notificationInfo.Url : ""}
            rules={{ ...validationRules.maxLength(250), }}
            onBlur={(e) => { setNotificationInfo({ ...notificationInfo, Url: e.target.value }) }}
          />
        </Grid>
      </Grid>
      <Stack direction='row' spacing={3} sx={{ mt: 3, width: '100%', justifyContent: 'end', '> button': { width: 150 } }}>
        {notificationId &&
          <MyMButton onClick={onClickDelete} >{controlSettings.LabelList.btnDelete}</MyMButton>}
        <MyMButton onClick={handleSubmit(handlePublish, onError)}>{controlSettings.LabelList.btnPublish}</MyMButton>
        <MyMButton onClick={handleModalClose}>{controlSettings.LabelList.btnClose}</MyMButton>
      </Stack>
    </Box>
  );
}

export default NotificationRegistration